import {
  Component,
  ChangeDetectionStrategy,
  inject,
  input,
  model,
  output,
  signal,
  linkedSignal,
  effect,
  DestroyRef,
} from '@angular/core';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, forkJoin } from 'rxjs';
import { debounceTime, map, switchMap, catchError, of } from 'rxjs';
import { Dialog } from 'primeng/dialog';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { FloatLabel } from 'primeng/floatlabel';
import { AutoComplete } from 'primeng/autocomplete';
import { Textarea } from 'primeng/textarea';
import { GooglePlacesService, PlacePrediction } from '../../services/google-places.service';
import { SearchService } from '../../services/search.service';
import {
  AddressLookupFacadeService,
  AddressCityAuxData,
} from '../../services/address-lookup-facade.service';
import { UnitFieldSpec } from '../../models/units.model';

type SelectOption = { label: string; value: string };

@Component({
  selector: 'app-unit-edit-dialog',
  imports: [
    ReactiveFormsModule,
    Dialog,
    Button,
    InputText,
    Select,
    FloatLabel,
    AutoComplete,
    Textarea,
  ],
  templateUrl: './unit-edit-dialog.component.html',
  styleUrl: './unit-edit-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnitEditDialogComponent {
  private readonly fb = inject(FormBuilder);
  private readonly googlePlacesService = inject(GooglePlacesService);
  private readonly searchService = inject(SearchService);
  private readonly addressFacade = inject(AddressLookupFacadeService);
  private readonly destroyRef = inject(DestroyRef);

  // ── Inputs / Outputs ────────────────────────────────────────────

  /** Two-way binding: open/close the dialog. */
  visible = model(false);

  /** Missing/invalid field specs to render as form fields. */
  readonly fieldSpecs = input.required<UnitFieldSpec[]>();

  /** Current raw unit data used to pre-populate each field. */
  readonly unitData = input<Record<string, string>>({});

  /** Mirrors a parent `saving` signal — shows spinner on Save button. */
  readonly saving = input(false);

  /** Dialog header. Parent can override based on unit type. */
  readonly headerTitle = input('Missing Claim Information');

  /** Descriptive instruction shown under the header. */
  readonly message = input(
    'This reserve has missing required information. Confirm the following details before proceeding with the new dispatch.',
  );

  /** Primary action button label. */
  readonly submitLabel = input('Continue with New Dispatch');

  /** Emits the patched field key→value map when user saves a valid form. */
  readonly saved = output<Record<string, string>>();

  /** Emits when user cancels without saving. */
  readonly cancelled = output<void>();

  // ── Internal state ───────────────────────────────────────────────

  readonly submitted = signal(false);
  readonly addressSuggestions = signal<string[]>([]);
  private readonly addressPredictions = signal<PlacePrediction[]>([]);
  private readonly addressSearch$ = new Subject<string>();
  private readonly addressSelect$ = new Subject<{ value: string; spec: UnitFieldSpec }>();

  // Dynamic dropdown options loaded once from the shared code tables (same source the
  // Units edit screen uses) so Color/Type/State render as real dropdowns.
  private readonly stateOptions = signal<SelectOption[]>([]);
  private readonly colorOptions = signal<SelectOption[]>([]);
  private readonly vehicleTypeOptions = signal<SelectOption[]>([]);

  // State-dependent City/Zip autocomplete options, mirroring the Units edit screen's
  // p-autocomplete dropdowns backed by AddressLookupFacadeService.
  readonly cityOptions = signal<string[]>([]);
  readonly zipOptions = signal<string[]>([]);
  private cityAuxData = new Map<string, AddressCityAuxData>();

  /**
   * Reactively rebuild the FormGroup whenever `fieldSpecs` or `unitData` changes.
   * When an 'address' spec is present, companion fields (state/city/zip) are also
   * added as controls so autocomplete can patch them even if they weren't missing.
   */
  readonly form = linkedSignal<FormGroup>(() => {
    const specs = this.fieldSpecs();
    const data = this.unitData();
    const controls: Record<string, unknown> = {};

    for (const spec of specs) {
      const validators = [Validators.required];
      if (spec.invalidFn) {
        const fn = spec.invalidFn;
        validators.push((ctrl: AbstractControl): ValidationErrors | null =>
          fn(ctrl.value?.trim?.() ?? '') ? { invalidValue: true } : null,
        );
      }
      controls[spec.key] = [data[spec.key] ?? '', validators];

      // Add companion controls (no validators) so autocomplete can patch them
      for (const companion of spec.companions ?? []) {
        if (!controls[companion]) {
          controls[companion] = [data[companion] ?? ''];
        }
      }
    }

    return this.fb.group(controls);
  });

  constructor() {
    effect(() => {
      this.form();
      this.submitted.set(false);
      this.addressSuggestions.set([]);
      this.addressPredictions.set([]);
    });

    this.loadLookupOptions();

    this.addressSearch$
      .pipe(
        debounceTime(300),
        switchMap((q) =>
          this.googlePlacesService.getPlacePredictions(q).pipe(catchError(() => of([]))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((predictions) => {
        this.addressPredictions.set(predictions);
        this.addressSuggestions.set(predictions.map((p) => p.description));
      });

    this.addressSelect$
      .pipe(
        switchMap(({ value, spec }) =>
          // Reuse the service's centralised predict-vs-geocode fallback (same path the shared
          // createAddressAutocomplete controller uses) instead of duplicating it here.
          this.googlePlacesService.resolveAddress(value, this.addressPredictions()).pipe(
            map((result) => ({ result, spec })),
            catchError(() => of(null)),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((payload) => {
        if (!payload) return;
        const { result, spec } = payload;
        const form = this.form();
        // p-autocomplete writes the full Google description (e.g. "750 Hommann Ave, Perth Amboy,
        // NJ, USA") into the address control on select. Overwrite it with the parsed street line
        // only, then fan the rest out to the companion city/state/zip/county/lat/long controls —
        // mirroring the shared createAddressAutocomplete controller used by the Units screen.
        form.get(spec.key)?.setValue(result.addressLine1 ?? '', { emitEvent: false });
        // Companion values are prefix-agnostic (veh*/prop*), so resolve each control by its suffix.
        const valueBySuffix: Record<string, string> = {
          StateCd: result.state,
          CityName: result.city,
          ZipCd: result.zipcode,
          SubdName: result.county ?? '',
          Lat: result.latitude != null ? String(result.latitude) : '',
          Long: result.longitude != null ? String(result.longitude) : '',
        };
        for (const key of spec.companions ?? []) {
          const suffix = Object.keys(valueBySuffix).find((s) => key.endsWith(s));
          if (suffix) form.get(key)?.setValue(valueBySuffix[suffix], { emitEvent: false });
        }
      });
  }

  // ── Lookups ──────────────────────────────────────────────────────

  /**
   * Loads the Color/State/Vehicle-Type code tables once (same SearchService sources
   * the Units edit screen uses) so those fields render as real dropdowns.
   */
  private loadLookupOptions(): void {
    forkJoin({
      states: this.searchService.getStateOptions(),
      colors: this.searchService.getVehicleColorCodes(),
      vehTypes: this.searchService.getVehicleTypeCodeDetails(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ states, colors, vehTypes }) => {
        this.stateOptions.set(states);
        this.colorOptions.set(
          Array.from(colors.entries()).map(([value, label]) => ({ label, value })),
        );
        this.vehicleTypeOptions.set(
          Array.from(vehTypes.entries()).map(([value, { label }]) => ({
            label: `${value} - ${label}`,
            value,
          })),
        );
      });
  }

  // ── Handlers ─────────────────────────────────────────────────────

  onSave(): void {
    this.submitted.set(true);
    if (this.form().invalid) return;
    this.saved.emit(this.form().getRawValue() as Record<string, string>);
  }

  onCancel(): void {
    this.visible.set(false);
    this.submitted.set(false);
    this.cancelled.emit();
  }

  onAddressSearch(event: { query: string }): void {
    this.addressSearch$.next(event.query ?? '');
  }

  onAddressSelect(event: { value: string }, spec: UnitFieldSpec): void {
    this.addressSelect$.next({ value: event.value ?? '', spec });
  }

  // ── City / Zip autocomplete (state-dependent, same as Units screen) ──

  /**
   * Derives the location field-key group (state/city/zip/county) from a city or zip key.
   * e.g. 'vehLocCityName' | 'vehLocZipCd' → prefix 'vehLoc'.
   */
  private locKeys(key: string): { state: string; city: string; zip: string; county: string } {
    const prefix = key.replace(/(CityName|ZipCd)$/, '');
    return {
      state: `${prefix}StateCd`,
      city: `${prefix}CityName`,
      zip: `${prefix}ZipCd`,
      county: `${prefix}SubdName`,
    };
  }

  private controlValue(key: string): string {
    return (this.form().get(key)?.value as string) ?? '';
  }

  onCitySearch(spec: UnitFieldSpec, query: string): void {
    const { state } = this.locKeys(spec.key);
    this.addressFacade
      .searchCities(query, this.controlValue(state))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.cityAuxData = result.cityAuxData;
        this.cityOptions.set(result.cityOptions);
      });
  }

  /** Selecting a city backfills the dependent State/Zip/County via the code-table aux data. */
  onCitySelect(spec: UnitFieldSpec, city: string): void {
    const { state, zip, county } = this.locKeys(spec.key);
    const patch = this.addressFacade.buildCitySelectionPatch(city, this.cityAuxData, {
      state,
      zip,
      county,
    });
    if (Object.keys(patch).length) this.form().patchValue(patch);
  }

  onZipSearch(spec: UnitFieldSpec, query: string): void {
    const { state, city } = this.locKeys(spec.key);
    this.addressFacade
      .searchZips(query, this.controlValue(state), this.controlValue(city))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((opts) => this.zipOptions.set(opts));
  }

  // ── Template helpers ─────────────────────────────────────────────

  hasError(key: string): boolean {
    return this.submitted() && !!this.form().get(key)?.invalid;
  }

  errorMessage(spec: UnitFieldSpec): string {
    const ctrl = this.form().get(spec.key);
    if (!ctrl || !this.submitted()) return '';
    if (ctrl.hasError('required')) return `${spec.label} is required.`;
    if (ctrl.hasError('invalidValue'))
      return spec.invalidMessage ?? `${spec.label} has an invalid value.`;
    return '';
  }

  fieldType(
    spec: UnitFieldSpec,
  ): 'state' | 'select' | 'city' | 'zip' | 'address' | 'textarea' | 'text' {
    return spec.type ?? 'text';
  }

  /**
   * Resolves the dropdown options for a field. Dynamic `optionsSource` fields pull from
   * the loaded code tables; everything else falls back to the spec's static `options`.
   */
  optionsFor(spec: UnitFieldSpec): SelectOption[] {
    switch (spec.optionsSource) {
      case 'state':
        return this.stateOptions();
      case 'color':
        return this.colorOptions();
      case 'vehicleType':
        return this.vehicleTypeOptions();
      default:
        return spec.options ?? [];
    }
  }

  /**
   * Whether a select dropdown should show a filter box. Only long lookup lists
   * benefit (State, Color); short static lists and Type are left unfiltered.
   */
  showFilter(spec: UnitFieldSpec): boolean {
    return spec.optionsSource === 'state' || spec.optionsSource === 'color';
  }

  /**
   * Legacy auxBinding: changing the location State clears the dependent City/Zip/County —
   * same behavior as the Units and Assignment-Details screens. Only the vehicle/property
   * location state drives this, never License Plate State.
   */
  onStateChange(key: string): void {
    if (!key.endsWith('LocStateCd')) return;
    const prefix = key.slice(0, -'StateCd'.length); // 'vehLoc' | 'propLoc'
    this.form().patchValue({
      [`${prefix}CityName`]: '',
      [`${prefix}ZipCd`]: '',
      [`${prefix}SubdName`]: '',
    });
    // State drives City/Zip lookups; stale option lists no longer apply.
    this.cityOptions.set([]);
    this.zipOptions.set([]);
  }
}
